import ContactsIcon from '@mui/icons-material/Contacts';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import BookIcon from '@mui/icons-material/Book';
import EventNoteIcon from '@mui/icons-material/EventNote';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import HeadsetMicIcon from '@mui/icons-material/HeadsetMic';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import NotificationsNoneIcon from '@mui/icons-material/NotificationsNone';

export const NOTIF_VIEW_ALL_PAGE_SIZE = 100;

/** API `relatedEntityType` values that imply booking vs enquiry */
const ENTITY_BOOKING = new Set(['booking', 'bookings']);
const ENTITY_ENQUIRY = new Set(['inquiry', 'inquiries', 'enquiry', 'enquiries']);

const normEntity = (relatedEntityType) =>
  String(relatedEntityType ?? '')
    .trim()
    .toLowerCase();

const resolveNotificationItems = (block, payload) => {
  if (Array.isArray(block.items)) {
    return block.items;
  }
  if (Array.isArray(payload.items)) {
    return payload.items;
  }
  return [];
};

/** Extract list + unread from GET /customer/notifications response shapes */
export const parseNotificationsResponse = (res) => {
  const root = res?.data;
  const payload = root?.payload ?? root?.data?.payload ?? root?.data ?? {};
  const unreadCount = Number(payload.unreadCount ?? 0) || 0;
  const block = payload.notifications && typeof payload.notifications === 'object' ? payload.notifications : {};
  const items = resolveNotificationItems(block, payload);
  const page = Number(block.page ?? payload.page ?? 1) || 1;
  const pageSize = Number(block.pageSize ?? payload.pageSize ?? 20) || 20;
  const total = Number(block.total ?? payload.total ?? items.length) || 0;
  return { unreadCount, items, page, pageSize, total };
};

const isBookingCategory = (category) => {
  const c = String(category || '');
  const lower = c.toLowerCase();
  if (/\bbooking\b/.test(lower)) return true;
  if (/\breservation\b/.test(lower)) return true;
  if (/\bappointment\b/.test(lower)) return true;
  if (/^slot/i.test(c) || /slotchange|slotupdate|slotcancel/i.test(lower)) return true;
  if (/calendarhold|calendarrelease/i.test(lower)) return true;
  return false;
};

/** API categories such as `ContactInquiry` — treat as enquiry, not contact list. */
const isEnquiryCategory = (category) => {
  const raw = String(category || '');
  const lower = raw.toLowerCase();
  if (/contactinquiry/i.test(raw)) return true;
  if (/\benquir/.test(lower)) return true;
  if (/\binquir/.test(lower)) return true;
  if (/messagefromcustomer|contactform|leadcapture/i.test(lower)) return true;
  return false;
};

/** API categories such as `ProviderContactApproved` — contact / provider-contact lifecycle. */
const isContactCategory = (category) => {
  const raw = String(category || '');
  const lower = raw.toLowerCase();
  if (isEnquiryCategory(category)) return false;
  if (/providercontactapproved|providercontactrejected|providercontactreject|providercontact/i.test(lower)) {
    return true;
  }
  if (/\bcontact\b|contactlist|contactupdate/i.test(lower)) return true;
  return false;
};

/**
 * Icons + colors for a notification row.
 * Uses `relatedEntityType` when present so Booking / Enquiry stay accurate even if `category` is generic.
 */
export const getNotificationVisual = (category, relatedEntityType) => {
  const entity = normEntity(relatedEntityType);
  if (ENTITY_BOOKING.has(entity)) {
    return { Icon: BookIcon, color: '#08284F', bg: '#FFF7ED' };
  }
  if (ENTITY_ENQUIRY.has(entity)) {
    return { Icon: MailOutlineIcon, color: '#4F46E5', bg: '#EEF2FF' };
  }

  if (isBookingCategory(category)) {
    return { Icon: BookIcon, color: '#08284F', bg: '#FFF7ED' };
  }
  if (isEnquiryCategory(category)) {
    return { Icon: MailOutlineIcon, color: '#4F46E5', bg: '#EEF2FF' };
  }

  const c = String(category || '').toLowerCase();
  if (/promo|offer|discount|campaign|deal/.test(c)) {
    return { Icon: LocalOfferIcon, color: '#FF8C42', bg: '#FFF5EF' };
  }
  if (/\bevent\b|calendarinvite|webinar/.test(c)) {
    return { Icon: EventNoteIcon, color: '#2563EB', bg: '#EFF6FF' };
  }
  if (isContactCategory(category)) {
    return { Icon: ContactsIcon, color: '#059669', bg: '#ECFDF5' };
  }
  if (/support|help|ticket/.test(c)) {
    return { Icon: HeadsetMicIcon, color: '#7C3AED', bg: '#F5F3FF' };
  }
  if (/user|signup|account|admin/.test(c)) {
    return { Icon: PersonAddIcon, color: '#1D4ED8', bg: '#EFF6FF' };
  }
  return { Icon: NotificationsNoneIcon, color: '#6B7280', bg: '#F3F4F6' };
};

export const matchesNotificationFilter = (category, filter, relatedEntityType) => {
  if (!filter) return true;
  const entity = normEntity(relatedEntityType);
  const c = String(category || '').toLowerCase();

  switch (filter) {
    case 'Booking':
      if (ENTITY_BOOKING.has(entity)) return true;
      return isBookingCategory(category);
    case 'Enquiry':
      if (ENTITY_ENQUIRY.has(entity)) return true;
      return isEnquiryCategory(category);
    case 'Promotion':
      return /promo|offer|discount|campaign|deal/.test(c);
    case 'Events':
      return /\bevent\b|calendarinvite|webinar/.test(c);
    case 'Contact':
      if (ENTITY_ENQUIRY.has(entity)) return false;
      if (isEnquiryCategory(category)) return false;
      return isContactCategory(category);
    case 'User':
      if (ENTITY_BOOKING.has(entity) || ENTITY_ENQUIRY.has(entity)) return false;
      return /user|signup|account|admin/.test(c);
    case 'Customer Support':
      return /support|help|ticket/.test(c);
    default:
      return true;
  }
};

/** Filter dropdown `value` → i18n key for label */
export const NOTIFICATION_FILTER_OPTIONS = [
  { value: '', labelKey: 'layout.notifications.filterAll' },
  { value: 'Promotion', labelKey: 'layout.notifications.filterPromotion' },
  { value: 'Events', labelKey: 'layout.notifications.filterEvents' },
  { value: 'Contact', labelKey: 'layout.notifications.filterContact' },
  { value: 'User', labelKey: 'layout.notifications.filterUser' },
  { value: 'Customer Support', labelKey: 'layout.notifications.filterSupport' },
  { value: 'Booking', labelKey: 'layout.notifications.filterBooking' },
  { value: 'Enquiry', labelKey: 'layout.notifications.filterEnquiry' },
];

/** All notifications page — category filter: all + booking + enquiry + contact. */
export const NOTIFICATION_PAGE_FILTER_OPTIONS = [
  { value: '', labelKey: 'layout.notifications.filterAllNotifications' },
  { value: 'Booking', labelKey: 'layout.notifications.filterBooking' },
  { value: 'Enquiry', labelKey: 'layout.notifications.filterEnquiry' },
  { value: 'Contact', labelKey: 'layout.notifications.filterContact' },
];

/** @deprecated use NOTIFICATION_PAGE_FILTER_OPTIONS */
export const NOTIFICATION_BOOKING_ENQUIRY_FILTER_OPTIONS = NOTIFICATION_PAGE_FILTER_OPTIONS;
